import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertCircle, Bot } from "lucide-react";

export default function Objections() {
  const [leadId, setLeadId] = useState("");
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    if (!leadId.trim()) { toast.error("Lead-ID erforderlich (Demo: aus Lead-Detailseite oder direkt im Lead bearbeiten)"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/handle-objection`, { text });
      setResult(r.data);
      toast.success(`Erkannt: ${r.data.objection_type}`);
    } catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="objections-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Einwandbehandlung</h1>
        <p className="mt-1 text-slate-600">Tippen Sie eine Kundenantwort ein, die KI erkennt den Einwand und schlägt eine Antwort vor.</p>
      </header>

      <Card className="border-slate-200 p-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Lead-ID</label>
          <Input data-testid="obj-lead-id" placeholder="lead_xxxxxxxx" value={leadId} onChange={(e) => setLeadId(e.target.value)} />
          <p className="text-xs text-slate-500 mt-1">Tipp: Schneller geht es direkt auf der Lead-Detailseite (Tab „Einwand").</p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Kundenantwort</label>
          <Textarea data-testid="obj-text" rows={5} placeholder='z.B. "Ist mir ehrlich gesagt zu teuer."' value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <Button data-testid="obj-analyze" onClick={analyze} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Bot className="w-4 h-4 mr-2" /> {busy ? "Analysiere …" : "Einwand analysieren"}
        </Button>
      </Card>

      {result && (
        <Card className="border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-emerald-700" />
            <p className="text-xs uppercase tracking-wider font-semibold text-emerald-800">Erkannter Einwand: {result.objection_type}</p>
          </div>
          <p className="text-sm text-slate-800 whitespace-pre-wrap">{result.suggested_reply}</p>
        </Card>
      )}

      <Card className="border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Erkennbare Einwände</h3>
        <div className="flex flex-wrap gap-2 text-xs">
          {["Preis", "Vergleichsangebot", "Finanzierung", "Förderung", "Technik", "Partner", "Timing", "Vertrauen"].map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{t}</span>
          ))}
        </div>
      </Card>
    </div>
  );
}
